import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getProfile, setProfile, clearAuth } from '../utils/tokenStorage';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on app load (cookie-based)
  useEffect(() => {
    const checkAuth = async () => {
      const userData = getProfile();

      if (userData) {
        try {
          // Cookie is sent automatically by the browser
          const response = await axios.get('/auth/me');
          if (response.data.success) {
            setUser(response.data.data);
          } else {
            clearAuth();
          }
        } catch (error) {
          logger.error('Auth verification error:', error);
          clearAuth();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { user_profile } = response.data.data;

        // Token is set as httpOnly cookie by backend — just store profile
        setProfile(user_profile);

        setUser(user_profile);
        return { 
          success: true, 
          forcePasswordChange: user_profile.force_password_change || false 
        };
      } else {
        return { success: false, error: response.data.message || 'Login failed' };
      }
    } catch (error) {
      logger.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Network error' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);

      if (response.data.success) {
        const { user_profile } = response.data.data;

        setProfile(user_profile);

        setUser(user_profile);
        return { success: true };
      } else {
        return { success: false, error: response.data.message || 'Registration failed' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Network error' 
      };
    }
  };

  const registerCompany = async (companyData) => {
    try {
      const response = await axios.post('/companies/register', companyData);

      if (response.data.success) {
        const { user_profile } = response.data.data;

        setProfile(user_profile);

        setUser(user_profile);
        return { success: true, data: response.data.data };
      } else {
        return { success: false, error: response.data.message || 'Company registration failed' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Network error' 
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post('/auth/logout');
    } catch {
      // Ignore errors — still clear local state
    }
    clearAuth();
    setUser(null);
  };

  const changePassword = async (newPassword, currentPassword = null) => {
    try {
      const response = await axios.put('/auth/change-password', {
        new_password: newPassword,
        current_password: currentPassword
      });

      if (response.data.success) {
        setUser(prev => prev ? { ...prev, force_password_change: false } : null);

        const storedProfile = getProfile();
        if (storedProfile) {
          setProfile({ ...storedProfile, force_password_change: false });
        }

        return { success: true };
      } else {
        return { success: false, error: response.data.message || 'Password change failed' };
      }
    } catch (error) {
      logger.error('Password change error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Network error' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    registerCompany,
    logout,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
